import type { Labelled } from './common.js';

/**
 * ثوابت المحاسبة العامة.
 */

/* ==========================================================================
   أنواع الحسابات
   ========================================================================== */

export const LEDGER_ACCOUNT_TYPES = [
  'ASSET',
  'LIABILITY',
  'EQUITY',
  'REVENUE',
  'EXPENSE',
] as const;
export type LedgerAccountType = (typeof LEDGER_ACCOUNT_TYPES)[number];

export interface LedgerTypeDef extends Labelled<LedgerAccountType> {
  /**
   * الرصيد الطبيعي للحساب.
   *
   * الأصول والمصروفات تزيد بالمدين، والالتزامات وحقوق الملكية والإيرادات
   * تزيد بالدائن. هذا ما يحدد إشارة الرصيد عند العرض: حساب دائن برصيد مدين
   * حالة شاذة تستحق الانتباه لا مجرد رقم سالب.
   */
  normalBalance: 'DEBIT' | 'CREDIT';
  /** يظهر في قائمة الدخل لا في الميزانية. */
  isIncomeStatement: boolean;
}

export const LEDGER_TYPE_DEFS: readonly LedgerTypeDef[] = [
  { key: 'ASSET', label: 'أصول', kind: 'info', normalBalance: 'DEBIT', isIncomeStatement: false },
  {
    key: 'LIABILITY',
    label: 'التزامات',
    kind: 'warning',
    normalBalance: 'CREDIT',
    isIncomeStatement: false,
  },
  {
    key: 'EQUITY',
    label: 'حقوق ملكية',
    kind: 'brand',
    normalBalance: 'CREDIT',
    isIncomeStatement: false,
  },
  {
    key: 'REVENUE',
    label: 'إيرادات',
    kind: 'success',
    normalBalance: 'CREDIT',
    isIncomeStatement: true,
  },
  {
    key: 'EXPENSE',
    label: 'مصروفات',
    kind: 'danger',
    normalBalance: 'DEBIT',
    isIncomeStatement: true,
  },
] as const;

export const LEDGER_TYPE_MAP = Object.fromEntries(
  LEDGER_TYPE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LedgerAccountType, LedgerTypeDef>>;

/* ==========================================================================
   الحسابات النظامية
   ========================================================================== */

/**
 * مفاتيح الحسابات التي يحتاجها الترحيل التلقائي.
 *
 * الترميز يختلف من شركة لأخرى، لكن الغرض ثابت. الربط بالمفتاح لا بالرقم
 * يسمح لكل شركة أن ترقّم دليلها كما تشاء دون كسر الترحيل.
 */
export const SYSTEM_ACCOUNTS = {
  CASH: 'النقدية بالخزينة',
  BANK: 'النقدية بالبنوك',
  RECEIVABLE: 'العملاء',
  PAYABLE: 'الموردون',
  VAT_OUTPUT: 'ضريبة القيمة المضافة المستحقة',
  VAT_INPUT: 'ضريبة القيمة المضافة المدفوعة',
  REVENUE: 'إيرادات الخدمات',
  DISCOUNT_GIVEN: 'خصم مسموح به',
  RETAINED_EARNINGS: 'الأرباح المرحّلة',
  OPENING_BALANCE: 'حساب أرصدة الافتتاح',
} as const;

export type SystemAccountKey = keyof typeof SYSTEM_ACCOUNTS;

/* ==========================================================================
   دليل الحسابات الافتراضي
   ========================================================================== */

export interface ChartNode {
  code: string;
  name: string;
  type: LedgerAccountType;
  isGroup?: boolean;
  systemKey?: SystemAccountKey;
  children?: ChartNode[];
}

/**
 * دليل حسابات مبدئي لشركة خدمية.
 *
 * مبني على ما تحتاجه شركة خدمات فعلًا لا على دليل صناعي عام: لا مخزون ولا
 * تكلفة بضاعة مباعة، وبدلًا منهما تكلفة خدمات مباشرة وأجور متعاونين.
 *
 * يُنسخ للمستأجر عند التهيئة ثم يعدّله كما يشاء: يضيف حسابات، ويعيد الترقيم،
 * ويعطّل ما لا يستخدمه. الحسابات النظامية وحدها لا تُحذف.
 */
export const DEFAULT_CHART: readonly ChartNode[] = [
  {
    code: '1',
    name: 'الأصول',
    type: 'ASSET',
    isGroup: true,
    children: [
      {
        code: '11',
        name: 'الأصول المتداولة',
        type: 'ASSET',
        isGroup: true,
        children: [
          { code: '1110', name: 'النقدية بالخزينة', type: 'ASSET', systemKey: 'CASH' },
          { code: '1120', name: 'النقدية بالبنوك', type: 'ASSET', systemKey: 'BANK' },
          { code: '1130', name: 'العملاء', type: 'ASSET', systemKey: 'RECEIVABLE' },
          { code: '1140', name: 'أوراق قبض', type: 'ASSET' },
          {
            code: '1150',
            name: 'ضريبة القيمة المضافة المدفوعة',
            type: 'ASSET',
            systemKey: 'VAT_INPUT',
          },
          { code: '1160', name: 'مصروفات مدفوعة مقدمًا', type: 'ASSET' },
          { code: '1170', name: 'عهد وسلف', type: 'ASSET' },
        ],
      },
      {
        code: '12',
        name: 'الأصول الثابتة',
        type: 'ASSET',
        isGroup: true,
        children: [
          { code: '1210', name: 'أثاث وتجهيزات', type: 'ASSET' },
          { code: '1220', name: 'أجهزة حاسب ومعدات', type: 'ASSET' },
          { code: '1230', name: 'سيارات', type: 'ASSET' },
          { code: '1290', name: 'مجمع الإهلاك', type: 'ASSET' },
        ],
      },
    ],
  },

  {
    code: '2',
    name: 'الالتزامات',
    type: 'LIABILITY',
    isGroup: true,
    children: [
      {
        code: '21',
        name: 'الالتزامات المتداولة',
        type: 'LIABILITY',
        isGroup: true,
        children: [
          { code: '2110', name: 'الموردون', type: 'LIABILITY', systemKey: 'PAYABLE' },
          { code: '2120', name: 'أوراق دفع', type: 'LIABILITY' },
          {
            code: '2130',
            name: 'ضريبة القيمة المضافة المستحقة',
            type: 'LIABILITY',
            systemKey: 'VAT_OUTPUT',
          },
          { code: '2140', name: 'ضرائب ودمغات مستحقة', type: 'LIABILITY' },
          { code: '2150', name: 'مرتبات مستحقة', type: 'LIABILITY' },
          { code: '2160', name: 'تأمينات اجتماعية مستحقة', type: 'LIABILITY' },
          { code: '2170', name: 'مصروفات مستحقة', type: 'LIABILITY' },
          { code: '2180', name: 'دفعات مقدمة من العملاء', type: 'LIABILITY' },
        ],
      },
    ],
  },

  {
    code: '3',
    name: 'حقوق الملكية',
    type: 'EQUITY',
    isGroup: true,
    children: [
      { code: '3110', name: 'رأس المال', type: 'EQUITY' },
      { code: '3120', name: 'جاري الشركاء', type: 'EQUITY' },
      {
        code: '3130',
        name: 'الأرباح المرحّلة',
        type: 'EQUITY',
        systemKey: 'RETAINED_EARNINGS',
      },
      {
        code: '3190',
        name: 'حساب أرصدة الافتتاح',
        type: 'EQUITY',
        systemKey: 'OPENING_BALANCE',
      },
    ],
  },

  {
    code: '4',
    name: 'الإيرادات',
    type: 'REVENUE',
    isGroup: true,
    children: [
      { code: '4110', name: 'إيرادات الخدمات', type: 'REVENUE', systemKey: 'REVENUE' },
      { code: '4120', name: 'إيرادات العقود', type: 'REVENUE' },
      { code: '4130', name: 'إيرادات أخرى', type: 'REVENUE' },
      { code: '4190', name: 'خصم مسموح به', type: 'REVENUE', systemKey: 'DISCOUNT_GIVEN' },
    ],
  },

  {
    code: '5',
    name: 'المصروفات',
    type: 'EXPENSE',
    isGroup: true,
    children: [
      {
        code: '51',
        name: 'تكلفة تقديم الخدمة',
        type: 'EXPENSE',
        isGroup: true,
        children: [
          { code: '5110', name: 'تكلفة خدمات مباشرة', type: 'EXPENSE' },
          { code: '5120', name: 'أجور متعاونين ومستقلين', type: 'EXPENSE' },
          { code: '5130', name: 'اشتراكات برامج وأدوات', type: 'EXPENSE' },
          { code: '5140', name: 'مصروفات إنتاج وتصوير', type: 'EXPENSE' },
        ],
      },
      {
        code: '52',
        name: 'مصروفات إدارية وعمومية',
        type: 'EXPENSE',
        isGroup: true,
        children: [
          { code: '5210', name: 'المرتبات والأجور', type: 'EXPENSE' },
          { code: '5220', name: 'مكافآت وحوافز', type: 'EXPENSE' },
          { code: '5230', name: 'إيجار', type: 'EXPENSE' },
          { code: '5240', name: 'كهرباء ومياه', type: 'EXPENSE' },
          { code: '5250', name: 'اتصالات وإنترنت', type: 'EXPENSE' },
          { code: '5260', name: 'قرطاسية ومطبوعات', type: 'EXPENSE' },
          { code: '5270', name: 'صيانة ونظافة', type: 'EXPENSE' },
          { code: '5280', name: 'سفر وانتقالات', type: 'EXPENSE' },
          { code: '5290', name: 'مصروفات إدارية أخرى', type: 'EXPENSE' },
        ],
      },
      {
        code: '53',
        name: 'مصروفات تسويقية',
        type: 'EXPENSE',
        isGroup: true,
        children: [
          { code: '5310', name: 'إعلانات مدفوعة', type: 'EXPENSE' },
          { code: '5320', name: 'مصروفات تسويق أخرى', type: 'EXPENSE' },
        ],
      },
      {
        code: '54',
        name: 'مصروفات مالية',
        type: 'EXPENSE',
        isGroup: true,
        children: [
          { code: '5410', name: 'مصروفات وعمولات بنكية', type: 'EXPENSE' },
          { code: '5420', name: 'فروق عملة', type: 'EXPENSE' },
          { code: '5430', name: 'إهلاك الأصول الثابتة', type: 'EXPENSE' },
        ],
      },
    ],
  },
] as const;

/** يفرد شجرة الدليل إلى قائمة مسطّحة مع مستوى كل حساب. */
export function flattenChart(
  nodes: readonly ChartNode[] = DEFAULT_CHART,
  parentCode: string | null = null,
  level = 0,
): Array<ChartNode & { parentCode: string | null; level: number }> {
  const out: Array<ChartNode & { parentCode: string | null; level: number }> = [];
  for (const node of nodes) {
    out.push({ ...node, parentCode, level });
    if (node.children) out.push(...flattenChart(node.children, node.code, level + 1));
  }
  return out;
}

/* ==========================================================================
   القيود
   ========================================================================== */

export const JOURNAL_SOURCES = [
  'MANUAL',
  'OPENING',
  'INVOICE',
  'PAYMENT',
  'BILL',
  'BILL_PAYMENT',
  'EXPENSE',
  'TRANSFER',
] as const;
export type JournalSource = (typeof JOURNAL_SOURCES)[number];

export const JOURNAL_SOURCE_LABELS: Readonly<Record<JournalSource, string>> = {
  MANUAL: 'قيد يدوي',
  OPENING: 'رصيد افتتاحي',
  INVOICE: 'فاتورة عميل',
  PAYMENT: 'تحصيل',
  BILL: 'فاتورة مورد',
  BILL_PAYMENT: 'سداد لمورد',
  EXPENSE: 'مصروف',
  TRANSFER: 'تحويل بين الخزائن',
};

export const JOURNAL_STATUSES = ['DRAFT', 'POSTED', 'VOID'] as const;
export type JournalStatus = (typeof JOURNAL_STATUSES)[number];

export const JOURNAL_STATUS_DEFS: readonly Labelled<JournalStatus>[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral' },
  { key: 'POSTED', label: 'مرحَّل', kind: 'success' },
  { key: 'VOID', label: 'ملغى', kind: 'danger' },
] as const;

export const JOURNAL_STATUS_MAP = Object.fromEntries(
  JOURNAL_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<JournalStatus, Labelled<JournalStatus>>>;

/**
 * مصادر القيود التي يملكها مستند.
 *
 * قيد الفاتورة أو المصروف صورةٌ عن مستنده: تعديل المستند يعكس قيده ويعيد
 * ترحيله من بياناته. لو عُدّل القيد مباشرة لاختلف الدفتر عن المستند، ثم
 * يمحو أول تعديلٍ على المستند التصحيحَ بصمت. فهذه تُصحَّح من مستندها.
 */
export const DOCUMENT_JOURNAL_SOURCES: readonly JournalSource[] = [
  'INVOICE',
  'PAYMENT',
  'BILL',
  'BILL_PAYMENT',
  'EXPENSE',
  'TRANSFER',
];

/* ==========================================================================
   سجل التعديلات — القيود والحسابات
   ========================================================================== */

export const LEDGER_HISTORY_ACTIONS = [
  'create',
  'update',
  'post',
  'reverse',
  'delete',
  'void',
  'legacy',
] as const;
export type LedgerHistoryAction = (typeof LEDGER_HISTORY_ACTIONS)[number];

/** تغيّر حقل واحد: العنوان والقيمة قبل وبعد، نصوصًا مقروءة كما كانت وقتها. */
export interface LedgerHistoryChange {
  label: string;
  from: string | null;
  to: string | null;
}

/** حدث في سجل تعديلات قيد أو حساب. */
export interface LedgerHistoryEvent {
  id: string;
  at: string;
  action: LedgerHistoryAction;
  actorId: string | null;
  actorName: string | null;
  /** جملة الحدث: «عُدّل القيد»، «رُحّل القيد»… */
  summary: string;
  changes: LedgerHistoryChange[];
  /** سبب أو ملاحظة: سبب العكس، المستند المولِّد… */
  note: string | null;
}

/* ==========================================================================
   الخزائن والموردون
   ========================================================================== */

export const CASH_ACCOUNT_KINDS = ['BANK', 'CASH', 'WALLET'] as const;
export type CashAccountKind = (typeof CASH_ACCOUNT_KINDS)[number];

export const CASH_ACCOUNT_KIND_LABELS: Readonly<Record<CashAccountKind, string>> = {
  BANK: 'حساب بنكي',
  CASH: 'خزينة نقدية',
  WALLET: 'محفظة إلكترونية',
};

export const BILL_STATUSES = [
  'DRAFT',
  'RECEIVED',
  'PARTIAL',
  'PAID',
  'OVERDUE',
  'CANCELLED',
] as const;
export type BillStatus = (typeof BILL_STATUSES)[number];

export const BILL_STATUS_DEFS: readonly Labelled<BillStatus>[] = [
  { key: 'DRAFT', label: 'مسودة', kind: 'neutral' },
  { key: 'RECEIVED', label: 'مستلمة', kind: 'info' },
  { key: 'PARTIAL', label: 'مسدَّدة جزئيًا', kind: 'warning' },
  { key: 'PAID', label: 'مسدَّدة بالكامل', kind: 'success' },
  { key: 'OVERDUE', label: 'متأخرة', kind: 'danger' },
  { key: 'CANCELLED', label: 'ملغاة', kind: 'neutral' },
] as const;

export const BILL_STATUS_MAP = Object.fromEntries(
  BILL_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<BillStatus, Labelled<BillStatus>>>;

/* ==========================================================================
   التحقق من توازن القيد
   ========================================================================== */

export interface JournalLineInput {
  accountId: string;
  debit?: number;
  credit?: number;
}

export interface BalanceCheck {
  balanced: boolean;
  totalDebit: number;
  totalCredit: number;
  difference: number;
  errors: string[];
}

/**
 * فحص توازن القيد.
 *
 * ثلاثة شروط لا واحد:
 *   - مجموع المدين يساوي مجموع الدائن.
 *   - كل بند إما مدين أو دائن لا كلاهما، وإلا التبس اتجاه الحركة.
 *   - لا بنود بصفر، فهي لا تضيف معنى وتشوّش القراءة.
 */
export function checkBalance(lines: readonly JournalLineInput[]): BalanceCheck {
  const errors: string[] = [];
  let totalDebit = 0;
  let totalCredit = 0;

  if (lines.length < 2) errors.push('القيد يحتاج بندين على الأقل.');

  lines.forEach((line, index) => {
    const debit = Number(line.debit ?? 0);
    const credit = Number(line.credit ?? 0);
    const position = index + 1;

    if (debit < 0 || credit < 0) errors.push(`البند ${position}: لا تُقبل قيمة سالبة.`);
    if (debit > 0 && credit > 0) {
      errors.push(`البند ${position}: لا يكون البند مدينًا ودائنًا في آن واحد.`);
    }
    if (debit === 0 && credit === 0) errors.push(`البند ${position}: قيمة البند صفر.`);

    totalDebit += debit;
    totalCredit += credit;
  });

  totalDebit = Math.round(totalDebit * 100) / 100;
  totalCredit = Math.round(totalCredit * 100) / 100;
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;

  // فرق أقل من قرش ناتج عن التقريب لا يُعدّ اختلالًا.
  const balanced = Math.abs(difference) < 0.005;
  if (!balanced) {
    errors.push(`القيد غير متوازن: فرق ${Math.abs(difference).toFixed(2)}.`);
  }

  return { balanced: balanced && errors.length === 0, totalDebit, totalCredit, difference, errors };
}

/** إشارة الرصيد حسب طبيعة الحساب: موجب يعني رصيدًا طبيعيًا. */
export function naturalBalance(
  type: LedgerAccountType,
  debit: number,
  credit: number,
): number {
  const normal = LEDGER_TYPE_MAP[type].normalBalance;
  return normal === 'DEBIT'
    ? Math.round((debit - credit) * 100) / 100
    : Math.round((credit - debit) * 100) / 100;
}
